import React, { useState, useMemo } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Badge,
    VStack,
    HStack,
    Text,
    Box,
    Grid,
    GridItem,
    Card,
    CardBody,
    useColorModeValue,
    Icon,
    Input,
    Select,
    Flex,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    StatArrow,
    SimpleGrid,
    FormControl,
    FormLabel,
    InputGroup,
    InputLeftElement,
    Container,
    Heading,
    CircularProgress,
    CircularProgressLabel,
    IconButton,
    Divider,
    Switch,
    Tag,
    TagLabel,
    TagLeftIcon,
    Collapse,
    Center
} from '@chakra-ui/react';
import { 
    MdApps, 
    MdCode, 
    MdWeb, 
    MdSearch,
    MdFilterList,
    MdBusiness,
    MdExtension,
    MdCheckCircle,
    MdCancel,
    MdGridView,
    MdList,
    MdExpandMore,
    MdExpandLess,
    MdUpdate,
    MdNewReleases,
    MdStorage,
    MdApi,
    MdSync
} from 'react-icons/md';

const AvailableApplicationsModal = ({
    isOpen,
    onClose,
    availableApplications = [],
    isLoading = false,
    clientName = ""
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [viewMode, setViewMode] = useState('grid');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [showFilters, setShowFilters] = useState(false);
    const [favorites, setFavorites] = useState(new Set());

    // Color mode values with enhanced gradients and glass effects
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headerBg = useColorModeValue('white', 'gray.800');
    const textPrimary = useColorModeValue('gray.900', 'white');
    const textSecondary = useColorModeValue('gray.600', 'gray.400');
    const textMuted = useColorModeValue('gray.500', 'gray.500');
    const glassEffect = useColorModeValue(
        'rgba(255, 255, 255, 0.9)',
        'rgba(26, 32, 44, 0.9)'
    );
    const gradientBg = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    const primaryGradient = 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)';
    const successGradient = 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
    const warningGradient = 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)';
    const errorGradient = 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)';

    const getAppTypeConfig = (appType) => {
        switch (appType?.toLowerCase()) {
            case 'web application':
                return { 
                    icon: MdWeb, 
                    color: 'blue.500', 
                    gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                    bg: 'blue.50',
                    label: 'Web Application',
                    description: 'Web-based Solution',
                    emoji: '🌐'
                };
            case 'war':
                return { 
                    icon: MdStorage, 
                    color: 'orange.500', 
                    gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    bg: 'orange.50',
                    label: 'WAR Package',
                    description: 'Web Application Archive',
                    emoji: '📦'
                };
            case 'console application':
                return { 
                    icon: MdCode, 
                    color: 'purple.500', 
                    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                    bg: 'purple.50',
                    label: 'Console App',
                    description: 'Command Line Application',
                    emoji: '⚡'
                };
            case 'plugins':
                return { 
                    icon: MdExtension, 
                    color: 'pink.500', 
                    gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
                    bg: 'pink.50',
                    label: 'Plugin',
                    description: 'Extension Module',
                    emoji: '🔌'
                };
            case 'api service':
                return { 
                    icon: MdApi, 
                    color: 'teal.500', 
                    gradient: 'linear-gradient(135deg, #14B8A6 0%, #0F766E 100%)',
                    bg: 'teal.50',
                    label: 'API Service',
                    description: 'Application Programming Interface',
                    emoji: '🔗'
                };
            case 'etl':
                return { 
                    icon: MdSync, 
                    color: 'cyan.500', 
                    gradient: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
                    bg: 'cyan.50',
                    label: 'ETL Process',
                    description: 'Extract Transform Load',
                    emoji: '⚙️'
                };
            case 'others':
                return { 
                    icon: MdApps, 
                    color: 'gray.500', 
                    gradient: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                    bg: 'gray.50',
                    label: 'Application',
                    description: 'Other Application Type',
                    emoji: '📱'
                };
            default:
                return { 
                    icon: MdApps, 
                    color: 'gray.500', 
                    gradient: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                    bg: 'gray.50',
                    label: 'Application',
                    description: 'Generic Application',
                    emoji: '📱'
                };
        }
    };

    // Filter and sort applications
    const filteredApplications = useMemo(() => {
        if (!availableApplications) return [];
        
        let filtered = availableApplications.filter(app => {
            const matchesSearch = app.appName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                app.appDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                app.appType.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesType = filterType === 'all' || app.appType === filterType;
            const matchesStatus = filterStatus === 'all' || 
                               (filterStatus === 'enabled' && app.isEnabled) ||
                               (filterStatus === 'disabled' && !app.isEnabled);

            return matchesSearch && matchesType && matchesStatus;
        });

        // Sort applications
        filtered.sort((a, b) => {
            let aVal = a[sortBy === 'name' ? 'appName' : sortBy];
            let bVal = b[sortBy === 'name' ? 'appName' : sortBy];
            
            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }
            
            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        return filtered;
    }, [availableApplications, searchTerm, filterType, filterStatus, sortBy, sortOrder]);

    // Statistics
    const stats = useMemo(() => {
        const enabledApps = availableApplications.filter(app => app.isEnabled);
        const total = availableApplications.length;
        const enabledCount = enabledApps.length;
        const disabledCount = total - enabledCount;
        
        const typeStats = availableApplications
            .reduce((acc, app) => {
                acc[app.appType] = (acc[app.appType] || 0) + 1;
                return acc;
            }, {});

        return {
            total,
            enabled: enabledCount,
            disabled: disabledCount,
            enabledPercentage: total > 0 ? Math.round((enabledCount / total) * 100) : 0,
            typeStats,
            mostPopularType: Object.keys(typeStats).reduce((a, b) => typeStats[a] > typeStats[b] ? a : b, '')
        };
    }, [availableApplications]);

    const uniqueTypes = useMemo(() => {
        return [...new Set(availableApplications.map(app => app.appType))];
    }, [availableApplications]);

    const toggleFavorite = (appId) => {
        const newFavorites = new Set(favorites);
        if (newFavorites.has(appId)) {
            newFavorites.delete(appId);
        } else {
            newFavorites.add(appId);
        }
        setFavorites(newFavorites);
    };

    if (isLoading) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} size="4xl">
                <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
                <ModalContent bg={bgColor} m={6} borderRadius="3xl" boxShadow="2xl">
                    <ModalHeader bg={headerBg} borderTopRadius="3xl" py={8}>
                        <Center>
                            <VStack spacing={4}>
                                <CircularProgress isIndeterminate color="blue.400" size="60px" />
                                <Text fontSize="lg" color={textPrimary}>Loading Application Catalog...</Text>
                            </VStack>
                        </Center>
                    </ModalHeader>
                    <ModalCloseButton />
                </ModalContent>
            </Modal>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl">
            <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(12px)" />
            <ModalContent 
                bg={bgColor} 
                m={4} 
                borderRadius="3xl" 
                boxShadow="2xl" 
                overflow="hidden" 
                maxH="90vh"
                display="flex"
                flexDirection="column"
            >
                <ModalHeader 
                    bg={gradientBg} 
                    color="white" 
                    borderTopRadius="3xl" 
                    py={6}
                    position="relative"
                    overflow="hidden"
                >
                    {/* Header Background Pattern */}
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        opacity={0.1}
                        backgroundImage="radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 50%, white 1px, transparent 1px)"
                        backgroundSize="100px 100px"
                    />
                    
                    <Flex justify="space-between" align="center" position="relative">
                        <VStack align="start" spacing={2}>
                            <HStack spacing={4}>
                                <Box
                                    p={3}
                                    bg="whiteAlpha.200"
                                    borderRadius="2xl"
                                    backdropFilter="blur(10px)"
                                    border="1px solid"
                                    borderColor="whiteAlpha.300"
                                >
                                    <Icon as={MdApps} boxSize={6} />
                                </Box>
                                <VStack align="start" spacing={1}>
                                    <Heading size="lg" fontWeight="800">
                                        {clientName} - Application Catalog
                                    </Heading>
                                    <Text fontSize="md" opacity={0.9} fontWeight="500">
                                        Browse and explore available applications
                                    </Text>
                                </VStack>
                            </HStack>
                        </VStack>

                        <VStack spacing={2}>
                            <HStack spacing={3}>
                                <Badge 
                                    px={3} 
                                    py={1} 
                                    borderRadius="full" 
                                    bg="whiteAlpha.200"
                                    color="white"
                                    fontSize="sm"
                                    fontWeight="600"
                                    backdropFilter="blur(10px)"
                                >
                                    {filteredApplications.length} Available
                                </Badge>
                                <Badge 
                                    px={3} 
                                    py={1} 
                                    borderRadius="full" 
                                    bg="whiteAlpha.200"
                                    color="white"
                                    fontSize="sm"
                                    fontWeight="600"
                                    backdropFilter="blur(10px)"
                                >
                                    {stats.enabled} Enabled
                                </Badge>
                            </HStack>
                        </VStack>
                    </Flex>
                </ModalHeader>
                <ModalCloseButton color="white" size="lg" />

                <ModalBody p={0} flex="1" overflow="hidden" display="flex" flexDirection="column">
                    {!availableApplications || availableApplications.length === 0 ? (
                        <Container centerContent py={16}>
                            <VStack spacing={6}>
                                <Box
                                    p={6}
                                    bg={gradientBg}
                                    borderRadius="full"
                                    color="white"
                                    boxShadow="xl"
                                >
                                    <Icon as={MdApps} boxSize={12} />
                                </Box>
                                <VStack spacing={3}>
                                    <Heading size="md" color={textPrimary}>No Applications Available</Heading>
                                    <Text color={textSecondary} textAlign="center" maxW="400px">
                                        The application catalog is currently empty. Check back later for new applications 
                                        or contact your administrator to add/map applications.
                                    </Text>
                                </VStack>
                            </VStack>
                        </Container>
                    ) : (
                        <VStack spacing={0} align="stretch" flex="1" overflow="hidden">
                            {/* Statistics Section */}
                            <Box bg={headerBg} px={6} py={4} borderBottom="1px" borderColor={borderColor} flexShrink={0}>
                                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                                    <Card bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white" borderRadius="xl">
                                        <CardBody p={4}>
                                            <Stat>
                                                <StatLabel fontSize="xs" opacity={0.9}>Total Apps</StatLabel>
                                                <StatNumber fontSize="2xl" fontWeight="800">{stats.total}</StatNumber>
                                                <StatHelpText color="whiteAlpha.800">
                                                    <StatArrow type="increase" />
                                                    In catalog
                                                </StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>

                                    <Card bg={successGradient} color="white" borderRadius="xl">
                                        <CardBody p={4}>
                                            <Stat>
                                                <StatLabel fontSize="xs" opacity={0.9}>Enabled</StatLabel>
                                                <StatNumber fontSize="2xl" fontWeight="800">{stats.enabled}</StatNumber>
                                                <StatHelpText color="whiteAlpha.800">
                                                    {stats.enabledPercentage}% of total
                                                </StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>

                                    <Card bg={warningGradient} color="white" borderRadius="xl">
                                        <CardBody p={4}>
                                            <Stat>
                                                <StatLabel fontSize="xs" opacity={0.9}>Disabled</StatLabel>
                                                <StatNumber fontSize="2xl" fontWeight="800">{stats.disabled}</StatNumber>
                                                <StatHelpText color="whiteAlpha.800">
                                                    Need attention
                                                </StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>

                                    <Card bg={cardBg} border="2px" borderColor="purple.200" borderRadius="xl">
                                        <CardBody p={4}>
                                            <VStack spacing={2}>
                                                <CircularProgress
                                                    value={stats.enabledPercentage}
                                                    color="purple.400"
                                                    size="40px"
                                                    thickness="8px"
                                                >
                                                    <CircularProgressLabel fontSize="xs" fontWeight="bold">
                                                        {stats.enabledPercentage}%
                                                    </CircularProgressLabel>
                                                </CircularProgress>
                                                <Text fontSize="xs" color={textMuted} textAlign="center">
                                                    Health Score
                                                </Text>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                </SimpleGrid>
                            </Box>

                            {/* Filters Section */}
                            <Box bg={headerBg} px={6} py={4} borderBottom="1px" borderColor={borderColor} flexShrink={0}>
                                <VStack spacing={3} align="stretch">
                                    <Flex justify="space-between" align="center">
                                        <HStack spacing={2}>
                                            <Icon as={MdFilterList} color="blue.500" boxSize={4} />
                                            <Text fontWeight="700" color={textPrimary} fontSize="md">Search & Filters</Text>
                                            {filteredApplications.length > 0 && (
                                                <Badge colorScheme="blue" fontSize="xs" px={2} py={1} borderRadius="full">
                                                    {filteredApplications.length} results
                                                </Badge>
                                            )}
                                        </HStack>
                                        <HStack spacing={2}>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                leftIcon={<Icon as={showFilters ? MdExpandLess : MdExpandMore} />}
                                                onClick={() => setShowFilters(!showFilters)}
                                            >
                                                Advanced
                                            </Button>
                                            <IconButton
                                                icon={<Icon as={viewMode === 'grid' ? MdList : MdGridView} />}
                                                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                                                size="sm"
                                                variant="ghost"
                                                aria-label="Toggle view"
                                            />
                                        </HStack>
                                    </Flex>

                                    <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
                                        <GridItem>
                                            <FormControl>
                                                <FormLabel fontSize="xs" color={textMuted} fontWeight="600">
                                                    SEARCH APPLICATIONS
                                                </FormLabel>
                                                <InputGroup>
                                                    <InputLeftElement>
                                                        <Icon as={MdSearch} color="gray.400" boxSize={4} />
                                                    </InputLeftElement>
                                                    <Input
                                                        placeholder="Search apps, descriptions, or types..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        size="sm"
                                                        bg={cardBg}
                                                        borderRadius="lg"
                                                        border="2px"
                                                        borderColor="transparent"
                                                        _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3182CE' }}
                                                    />
                                                </InputGroup>
                                            </FormControl>
                                        </GridItem>
                                        
                                        <GridItem>
                                            <FormControl>
                                                <FormLabel fontSize="xs" color={textMuted} fontWeight="600">
                                                    APPLICATION TYPE
                                                </FormLabel>
                                                <Select
                                                    value={filterType}
                                                    onChange={(e) => setFilterType(e.target.value)}
                                                    size="sm"
                                                    bg={cardBg}
                                                    borderRadius="lg"
                                                    border="2px"
                                                    borderColor="transparent"
                                                    _focus={{ borderColor: 'blue.400' }}
                                                >
                                                    <option value="all">All Types</option>
                                                    {uniqueTypes.map(type => (
                                                        <option key={type} value={type}>
                                                            {getAppTypeConfig(type).label}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </GridItem>

                                        <GridItem>
                                            <FormControl>
                                                <FormLabel fontSize="xs" color={textMuted} fontWeight="600">
                                                    STATUS FILTER
                                                </FormLabel>
                                                <Select
                                                    value={filterStatus}
                                                    onChange={(e) => setFilterStatus(e.target.value)}
                                                    size="sm"
                                                    bg={cardBg}
                                                    borderRadius="lg"
                                                    border="2px"
                                                    borderColor="transparent"
                                                    _focus={{ borderColor: 'blue.400' }}
                                                >
                                                    <option value="all">All Status</option>
                                                    <option value="enabled">Enabled Only</option>
                                                    <option value="disabled">Disabled Only</option>
                                                </Select>
                                            </FormControl>
                                        </GridItem>
                                    </Grid>

                                    <Collapse in={showFilters} animateOpacity>
                                        <Grid templateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap={3} pt={3}>
                                            <GridItem>
                                                <FormControl>
                                                    <FormLabel fontSize="xs" color={textMuted} fontWeight="600">SORT BY</FormLabel>
                                                    <Select
                                                        value={sortBy}
                                                        onChange={(e) => setSortBy(e.target.value)}
                                                        size="sm"
                                                        bg={cardBg}
                                                        borderRadius="lg"
                                                    >
                                                        <option value="name">Name</option>
                                                        <option value="appType">Type</option>
                                                        <option value="appVersion">Version</option>
                                                        <option value="mappingVersion">Mapping Version</option>
                                                    </Select>
                                                </FormControl>
                                            </GridItem>
                                            <GridItem>
                                                <FormControl display="flex" alignItems="center" pt={5}>
                                                    <Switch
                                                        isChecked={sortOrder === 'desc'}
                                                        onChange={(e) => setSortOrder(e.target.checked ? 'desc' : 'asc')}
                                                        colorScheme="blue"
                                                        size="sm"
                                                    />
                                                    <FormLabel ml={2} mb={0} fontSize="xs">
                                                        Descending Order
                                                    </FormLabel>
                                                </FormControl>
                                            </GridItem>
                                        </Grid>
                                    </Collapse>
                                </VStack>
                            </Box>

                            {/* Applications Grid - Scrollable */}
                            <Box 
                                flex="1" 
                                overflow="auto"
                                minH="400px"
                                maxH="500px"
                                css={{
                                    '&::-webkit-scrollbar': {
                                        width: '12px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'rgba(0,0,0,0.05)',
                                        borderRadius: '10px',
                                        margin: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        borderRadius: '10px',
                                        border: '2px solid transparent',
                                        backgroundClip: 'content-box',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                        backgroundClip: 'content-box',
                                    },
                                }}
                            >
                                <Box p={6}>
                                    {viewMode === 'grid' ? (
                                    <Grid templateColumns="repeat(auto-fill, minmax(320px, 1fr))" gap={6}>
                                        {filteredApplications.map((app) => {
                                            const config = getAppTypeConfig(app.appType);
                                            const isFavorite = favorites.has(app.appId);
                                            
                                            return (
                                                <Card
                                                    key={app.appId}
                                                    bg={cardBg}
                                                    borderRadius="2xl"
                                                    overflow="hidden"
                                                    border="2px solid transparent"
                                                    _hover={{
                                                        transform: "translateY(-4px) scale(1.01)",
                                                        boxShadow: "0 15px 35px -12px rgba(0, 0, 0, 0.25)",
                                                        borderColor: config.color
                                                    }}
                                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                                    position="relative"
                                                >
                                                    {/* Gradient Header */}
                                                    <Box
                                                        h="100px"
                                                        bg={config.gradient}
                                                        position="relative"
                                                        overflow="hidden"
                                                    >
                                                        {/* Background Pattern */}
                                                        <Box
                                                            position="absolute"
                                                            top={0}
                                                            left={0}
                                                            right={0}
                                                            bottom={0}
                                                            opacity={0.15}
                                                            backgroundImage="radial-gradient(circle at 25% 25%, white 2px, transparent 2px)"
                                                            backgroundSize="30px 30px"
                                                        />
                                                        
                                                        {/* Status Badge */}
                                                        <Box position="absolute" top={3} right={3}>
                                                            <Badge
                                                                colorScheme={app.isEnabled ? 'green' : 'red'}
                                                                borderRadius="full"
                                                                px={2}
                                                                py={1}
                                                                fontSize="xs"
                                                                fontWeight="bold"
                                                                bg={app.isEnabled ? 'whiteAlpha.900' : 'whiteAlpha.900'}
                                                                color={app.isEnabled ? 'green.600' : 'red.600'}
                                                            >
                                                                <HStack spacing={1}>
                                                                    <Icon as={app.isEnabled ? MdCheckCircle : MdCancel} boxSize={3} />
                                                                    <Text>{app.isEnabled ? 'Enabled' : 'Disabled'}</Text>
                                                                </HStack>
                                                            </Badge>
                                                        </Box>

                                                        {/* App Icon and Type */}
                                                        <Center h="full">
                                                            <VStack spacing={2}>
                                                                <Box
                                                                    p={3}
                                                                    bg="whiteAlpha.200"
                                                                    borderRadius="xl"
                                                                    backdropFilter="blur(10px)"
                                                                    border="2px solid"
                                                                    borderColor="whiteAlpha.300"
                                                                >
                                                                    <Icon as={config.icon} boxSize={6} color="white" />
                                                                </Box>
                                                                <Badge
                                                                    bg="whiteAlpha.200"
                                                                    color="white"
                                                                    borderRadius="full"
                                                                    px={2}
                                                                    py={1}
                                                                    fontSize="xs"
                                                                    fontWeight="bold"
                                                                    backdropFilter="blur(10px)"
                                                                >
                                                                    {config.label}
                                                                </Badge>
                                                            </VStack>
                                                        </Center>
                                                    </Box>

                                                    <CardBody p={5}>
                                                        <VStack spacing={3} align="stretch">
                                                            {/* App Name and Favorite */}
                                                            <Flex justify="space-between" align="start">
                                                                <VStack align="start" spacing={1} flex="1">
                                                                    <Heading size="sm" color={textPrimary} noOfLines={2} fontWeight="800">
                                                                        {app.appName}
                                                                    </Heading>
                                                                    <Text fontSize="xs" color={textSecondary} noOfLines={2} lineHeight="tall">
                                                                        {app.appDescription || 'No description available'}
                                                                    </Text>
                                                                </VStack>
                                                            </Flex>

                                                            <Divider />

                                                            {/* App Details */}
                                                            <SimpleGrid columns={2} spacing={3}>
                                                                <Box>
                                                                    <Text fontSize="xs" color={textMuted} fontWeight="600" mb={1}>
                                                                        APP VERSION
                                                                    </Text>
                                                                    <Tag colorScheme="blue" borderRadius="full" size="sm">
                                                                        <TagLeftIcon as={MdNewReleases} />
                                                                        <TagLabel fontFamily="mono" fontWeight="bold" fontSize="xs">
                                                                            {app.appVersion}
                                                                        </TagLabel>
                                                                    </Tag>
                                                                </Box>
                                                                <Box>
                                                                    <Text fontSize="xs" color={textMuted} fontWeight="600" mb={1}>
                                                                        MAPPING VERSION
                                                                    </Text>
                                                                    <Tag colorScheme="purple" borderRadius="full" size="sm">
                                                                        <TagLeftIcon as={MdUpdate} />
                                                                        <TagLabel fontFamily="mono" fontWeight="bold" fontSize="xs">
                                                                            {app.mappingVersion||app.appVersion}
                                                                        </TagLabel>
                                                                    </Tag>
                                                                </Box>
                                                            </SimpleGrid>
                                                        </VStack>
                                                    </CardBody>
                                                </Card>
                                            );
                                        })}
                                    </Grid>
                                ) : (
                                    <VStack spacing={3}>
                                        {filteredApplications.map((app, index) => {
                                            const config = getAppTypeConfig(app.appType);
                                            return (
                                                <Card
                                                    key={app.appId}
                                                    bg={cardBg}
                                                    borderRadius="lg"
                                                    w="full"
                                                    border="1px"
                                                    borderColor={borderColor}
                                                    _hover={{ 
                                                        borderColor: config.color,
                                                        boxShadow: 'md'
                                                    }}
                                                    transition="all 0.3s"
                                                >
                                                    <CardBody py={4}>
                                                        <Flex justify="space-between" align="center">
                                                            <HStack spacing={4} flex="1">
                                                                <Text fontSize="xs" color={textMuted} w="30px">
                                                                    #{index + 1}
                                                                </Text>
                                                                <Box
                                                                    p={2}
                                                                    bg={config.bg}
                                                                    borderRadius="lg"
                                                                    color={config.color}
                                                                >
                                                                    <Icon as={config.icon} boxSize={5} />
                                                                </Box>
                                                                <VStack align="start" spacing={1} flex="1">
                                                                    <Text fontWeight="bold" fontSize="md" color={textPrimary}>
                                                                        {app.appName}
                                                                    </Text>
                                                                    <HStack spacing={2}>
                                                                        <Text fontSize="xs" color={textSecondary} noOfLines={1}>
                                                                            {config.label}
                                                                        </Text>
                                                                        <Text fontSize="xs" color={textMuted}>•</Text>
                                                                        <Text fontSize="xs" color={textSecondary} noOfLines={1}>
                                                                            App: {app.appVersion}
                                                                        </Text>
                                                                        <Text fontSize="xs" color={textMuted}>•</Text>
                                                                        <Text fontSize="xs" color={textSecondary} noOfLines={1}>
                                                                            Mapping: {app.mappingVersion}
                                                                        </Text>
                                                                    </HStack>
                                                                </VStack>
                                                            </HStack>
                                                            <HStack spacing={3}>
                                                                <Badge
                                                                    colorScheme={app.isEnabled ? 'green' : 'red'}
                                                                    variant="subtle"
                                                                    px={2}
                                                                    py={1}
                                                                    borderRadius="full"
                                                                    fontSize="xs"
                                                                >
                                                                    {app.isEnabled ? 'Enabled' : 'Disabled'}
                                                                </Badge>
                                                                <Tag colorScheme="blue" size="sm" borderRadius="full">
                                                                    <TagLabel fontSize="xs">App: {app.appVersion}</TagLabel>
                                                                </Tag>
                                                                <Tag colorScheme="purple" size="sm" borderRadius="full">
                                                                    <TagLabel fontSize="xs">Map: {app.mappingVersion}</TagLabel>
                                                                </Tag>
                                                            </HStack>
                                                        </Flex>
                                                    </CardBody>
                                                </Card>
                                            );
                                        })}
                                    </VStack>
                                )}

                                {/* No Results */}
                                {filteredApplications.length === 0 && (
                                    <Container centerContent py={12}>
                                        <VStack spacing={4}>
                                            <Box
                                                p={6}
                                                bg={gradientBg}
                                                borderRadius="full"
                                                color="white"
                                                boxShadow="lg"
                                            >
                                                <Icon as={MdSearch} boxSize={10} />
                                            </Box>
                                            <VStack spacing={2}>
                                                <Heading size="md" color={textPrimary}>
                                                    No Applications Found
                                                </Heading>
                                                <Text color={textSecondary} textAlign="center" maxW="300px" fontSize="sm">
                                                    No applications match your current search and filter criteria. 
                                                    Try adjusting your filters or search terms.
                                                </Text>
                                            </VStack>
                                            <Button
                                                bg={primaryGradient}
                                                color="white"
                                                borderRadius="lg"
                                                size="sm"
                                                onClick={() => {
                                                    setSearchTerm('');
                                                    setFilterType('all');
                                                    setFilterStatus('all');
                                                    setShowFilters(false);
                                                }}
                                            >
                                                Clear All Filters
                                            </Button>
                                        </VStack>
                                    </Container>
                                )}
                                </Box>
                            </Box>
                        </VStack>
                    )}
                </ModalBody>

                <ModalFooter 
                    bg={headerBg} 
                    borderTop="1px" 
                    borderColor={borderColor} 
                    borderBottomRadius="3xl"
                    py={4}
                    flexShrink={0}
                >
                    <Flex justify="space-between" align="center" w="full">
                        <HStack spacing={3}>
                            <Text fontSize="xs" color={textSecondary}>
                                Last updated: {new Date().toLocaleTimeString()}
                            </Text>
                            <Badge colorScheme="green" variant="subtle" borderRadius="full" fontSize="xs">
                                Live Catalog
                            </Badge>
                        </HStack>
                        <HStack spacing={2}>
                            <Text fontSize="xs" color={textSecondary}>
                                Showing {filteredApplications.length} of {stats.total} applications
                            </Text>
                            <Button 
                                onClick={onClose} 
                                bg={primaryGradient}
                                color="white"
                                borderRadius="lg"
                                size="md"
                                px={6}
                                _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                                transition="all 0.3s"
                            >
                                Close
                            </Button>
                        </HStack>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default AvailableApplicationsModal;